import React, { useCallback, useRef } from 'react';
import { Button, Row, Col } from 'reactstrap';
import Dropzone from 'react-dropzone';
import _ from 'lodash';
import { Field } from 'react-final-form';
import { connect } from 'react-redux';

import { Intl, ModalForm, Select } from 'components';
import { handlePromise, getCountryName } from 'utils';
import ListPhoneNumbersService from 'api/ListPhoneNumbers';
import SettingsActions from 'store/reducers/Settings';
import { required } from 'config/InputErrors';
import { useFetch } from 'hooks';
import RateService from 'api/Rate';

const UploadExcelContactModal = ( {
  open, locale, onReload, onClose, toggleLoading, toggleErrorAlert,
} ) => {
  const modalFormRef = useRef( null );
  const [rates, dataLoading] = useFetch( {
    initialState: [],
    promise: () => RateService.getRates( { 'filters[active]': '1', limit: -1 } ),
    format: ( dataToFormat ) => _.map( dataToFormat.data, ( item ) => {
      const country = getCountryName( item.country, locale );
      const codePhone = item.codePhone ? `(+${item.codePhone})` : '';
      return {
        ...item,
        countryName: `${country || item.country || '-'} ${codePhone}`,
      };
    } ),
    toggleErrorAlert,
    conditional: open,
    deps: [open],
    reInit: true,
  } );

  const submitForm = useCallback( async ( formData ) => {
    toggleLoading( true );
    const [errors, response, responseData] = await handlePromise(
      ListPhoneNumbersService.saveListPhoneNumbers( {
        originalName: formData.file.name,
        file: formData.file,
        countryCode: formData.phoneNumberCountryCode,
      } ),
    );
    toggleLoading( false );
    if ( !response.ok ) return _.isString( errors ) ? errors : 'apiError';

    const duplicateNumbers = responseData && responseData.duplicateNumbers
    && responseData.duplicateNumbers.length ? responseData.duplicateNumbers : null;

    const invalidNumbers = responseData && responseData.invalidNumbers
    && responseData.invalidNumbers.length ? responseData.invalidNumbers : null;

    return onReload( duplicateNumbers, invalidNumbers );
  }, [onReload, toggleLoading] );

  return (
    <ModalForm
      title="uploadPhonesFile"
      showErrorsInHeader
      bodyClassName="overflow-visible"
      bodyStyle={{ minHeight: 95 }}
      size="md"
      open={open}
      onClose={onClose}
      isLoading={dataLoading}
      onSubmit={submitForm}
      body={( modalForm ) => {
        modalFormRef.current = modalForm;
        return (
          <Row className="py-5">
            <Col md={10} className="m-auto">
              <Select
                field="country"
                placeholder="selectCountry"
                options={rates}
                labelKey="countryName"
                idKey="country"
                validate={required}
                onChange={( value, previousValue, valueItem ) => {
                  if ( value && valueItem ) {
                    modalForm.change( 'phoneNumberCountryCode', valueItem.original.codePhone );
                  }
                }}
              />
              <Field name="file" validate={required}>
                {( { meta, input } ) => {
                  const isInvalid = ( meta.touched && meta.error )
                    || ( meta.touched && !meta.dirtySinceLastSubmit && !!meta.submitError );
                  return (
                    <Dropzone
                      accept=".csv,.xls,.xlsx,.txt"
                      onDrop={( files ) => {
                        if ( files && files[0] ) {
                          input.onChange( files[0] );
                        }
                      }}
                    >
                      {( { getRootProps, getInputProps } ) => (
                        <div {...getRootProps()}>
                          <input {...getInputProps()} />
                          <Button
                            color="app-default"
                            size="app-sm"
                            type="button"
                            block
                          >
                            <Intl id="selectFile" />
                          </Button>

                          {isInvalid
                          && (
                            <div className="invalid-feedback d-block">
                              <Intl id={meta.error || meta.submitError} />
                            </div>
                          )}
                          {input.value
                            ? <p className="text-center text-sm text-mp-orange-1 mb-0 mt-2">{input.value.name}</p> : ''}
                        </div>
                      )}
                    </Dropzone>
                  );
                }}
              </Field>
            </Col>
          </Row>
        );
      }}
    />
  );
};

const mapStateToProps = ( { settings } ) => ( {
  locale: _.get( settings, 'locale.locale' ),
} );

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
} );

export default connect( mapStateToProps, mapDispatchToProps )( UploadExcelContactModal );
